import { useCallback, useSyncExternalStore } from 'react';

/**
 * Unsaved task-action input (review message, manager request, cancellation reason, personal note).
 *
 * Every draft has one deliberate owner: authenticated staff + task + action type. Drafts live
 * outside of component state so that
 *  - navigating task A -> B can never attach A's text to B's controls (B reads its own key),
 *  - a failed or abandoned A draft is recovered when A is revisited,
 *  - a late response for A can settle A's draft even when B is on screen,
 *  - a background refetch of the same task never touches dirty input.
 */
export type TaskDraftAction = 'review' | 'manager-request' | 'cancel' | 'note';

export interface TaskDraft {
  value: string;
  /**
   * Personal note only: the server text the draft was started from. When the server text moves
   * away from this base while the draft is dirty, the user has to reconcile explicitly.
   */
  base: string | null;
}

const SEP = '\u0000';

export function taskDraftKey(owner: string, taskId: string, action: TaskDraftAction): string {
  return [owner, taskId, action].join(SEP);
}

export function createTaskDraftStore() {
  const drafts = new Map<string, TaskDraft>();
  const listeners = new Set<() => void>();
  // Bumped whenever the staff session ends. Requests sent during an earlier session carry the
  // epoch they were sent in and must not surface (toast, close a view) in a later one.
  let epoch = 0;
  const emit = () => listeners.forEach((listener) => listener());

  return {
    get: (key: string): TaskDraft | undefined => drafts.get(key),
    set(key: string, draft: TaskDraft) {
      drafts.set(key, draft);
      emit();
    },
    clear(key: string) {
      if (drafts.delete(key)) emit();
    },
    /**
     * Settle a draft after the server accepted `submittedRaw`. A draft that still equals what was
     * sent is done; one the user kept editing is rebased so it stays a deliberate, dirty draft.
     */
    settle(key: string, submittedRaw: string) {
      const current = drafts.get(key);
      if (!current) return;
      if (current.value === submittedRaw) {
        drafts.delete(key);
      } else {
        drafts.set(key, { ...current, base: submittedRaw });
      }
      emit();
    },
    /** Forget every draft that does not belong to `owner` (sign-out or account switch). */
    clearOtherOwners(owner: string) {
      let changed = false;
      for (const key of Array.from(drafts.keys())) {
        if (!key.startsWith(owner + SEP)) {
          drafts.delete(key);
          changed = true;
        }
      }
      if (changed) emit();
    },
    /** Current session epoch; a request is stale once this differs from the value it was sent with. */
    epoch: (): number => epoch,
    /** The staff session ended (sign-out / no user): drop every draft and orphan in-flight requests. */
    reset() {
      epoch += 1;
      if (drafts.size === 0) return;
      drafts.clear();
      emit();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type TaskDraftStore = ReturnType<typeof createTaskDraftStore>;

export const opsTaskDrafts: TaskDraftStore = createTaskDraftStore();

export function useTaskDraft(owner: string, taskId: string, action: TaskDraftAction, store: TaskDraftStore = opsTaskDrafts) {
  const key = taskDraftKey(owner, taskId, action);
  const draft = useSyncExternalStore(
    store.subscribe,
    () => store.get(key),
    () => undefined,
  );
  const set = useCallback((value: string, base: string | null = null) => store.set(key, { value, base }), [store, key]);
  const clear = useCallback(() => store.clear(key), [store, key]);
  return { draft, set, clear, key };
}
